import { Directive, Input, OnDestroy, TemplateRef, ViewContainerRef, inject } from '@angular/core';
import { ConnectedPosition, ConnectionPositionPair, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

/**
 * Directive to enable the display of an overlay.
 *
 * Usage: Apply this directive to an ng-template and provide an origin element
 * for which the overlay should be attached to. In addition, supply a boolean
 * property 'show' to control the display of the overlay.
 */
@Directive({
    selector: '[apxOverlay]',
    standalone: true
})
export class ApxOverlayDirective implements OnDestroy {
    private readonly overlay = inject(Overlay);
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly templateRef = inject<TemplateRef<unknown>>(TemplateRef);

    @Input() set show(show: boolean) {
        if (show) {
            this.showOverlay();
        } else {
            this.hideOverlay();
        }
    }
    @Input() origin!: Element;
    @Input() panelClass = '';
    @Input() position: 'above ' | 'below' | 'after' | 'before' = 'after';
    @Input() offsetY = 0;
    @Input() offsetX = 0;

    private overlayRef!: OverlayRef;
    private portal!: TemplatePortal<unknown>;

    ngOnDestroy(): void {
        this.hideOverlay();
    }

    /**
     * Create the overlay and associate the template ref the directive is attached to
     * with it via a portal
     */
    private showOverlay(): void {
        if (!this.overlayRef) {
            this.overlayRef = this.createOverlay();
        }

        if (!this.overlayRef.hasAttached()) {
            this.portal = new TemplatePortal(this.templateRef, this.viewContainerRef);
            this.overlayRef.attach(this.portal);
        }
    }

    /**
     * Detach the overlay from the portal
     */
    private hideOverlay(): void {
        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
        }
    }

    /**
     * Create the overlay by providing a position strategy, this considers
     * the position passed in through directive configuration
     * @returns OverlayRef
     */
    private createOverlay(): OverlayRef {
        return this.overlay.create({
            panelClass: ['apx-element', this.panelClass],
            positionStrategy: this.overlay.position().flexibleConnectedTo(this.origin).withFlexibleDimensions(true).withPositions(this.getPositions()).withPush(true)
        });
    }

    /**
     * Get the positions by using the configuration passed to the directive
     * @returns ConnectedPosition[]
     */
    private getPositions(): ConnectedPosition[] {
        const positions: { [key: string]: ConnectedPosition[] } = {
            above: [
                new ConnectionPositionPair(
                    {
                        originX: 'start',
                        originY: 'top'
                    },
                    {
                        overlayX: 'start',
                        overlayY: 'bottom'
                    }
                ),
                new ConnectionPositionPair(
                    {
                        originX: 'start',
                        originY: 'bottom'
                    },
                    {
                        overlayX: 'start',
                        overlayY: 'top'
                    }
                )
            ],
            below: [
                new ConnectionPositionPair(
                    {
                        originX: 'start',
                        originY: 'bottom'
                    },
                    {
                        overlayX: 'start',
                        overlayY: 'top'
                    }
                ),
                new ConnectionPositionPair(
                    {
                        originX: 'start',
                        originY: 'top'
                    },
                    {
                        overlayX: 'start',
                        overlayY: 'bottom'
                    }
                )
            ],
            before: [
                new ConnectionPositionPair(
                    {
                        originX: 'start',
                        originY: 'top'
                    },
                    { overlayX: 'end', overlayY: 'top' }
                ),
                new ConnectionPositionPair(
                    {
                        originX: 'end',
                        originY: 'top'
                    },
                    { overlayX: 'start', overlayY: 'top' }
                )
            ],
            after: [
                new ConnectionPositionPair(
                    {
                        originX: 'end',
                        originY: 'top'
                    },
                    { overlayX: 'start', overlayY: 'top' }
                ),
                new ConnectionPositionPair(
                    {
                        originX: 'start',
                        originY: 'top'
                    },
                    { overlayX: 'end', overlayY: 'top' }
                )
            ]
        };

        return positions[this.position];
    }
}
